<app-page-shell>
  <app-page-hero
    eyebrow="Transport Planning"
    [title]="isNew ? 'Run Bulk Plan' : run ? (run.runNumber || 'Bulk Plan Run') : 'Bulk Planning Runs'"
    [subtitle]="isNew
      ? 'Choose demand and capacity scope, then let the optimizer consolidate and assign the fleet.'
      : run
        ? 'Review scenarios, resolve exceptions, lock accepted routes, and create execution records.'
        : 'Trace every planning run from an immutable demand snapshot through shipment creation.'">
    <div actions>
      @if (run) {
        <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true" (onClick)="refreshRun()"></p-button>
        @if (run.status === 'FAILED') {
          <p-button label="Retry" icon="pi pi-replay" severity="secondary" [outlined]="true" [loading]="isSaving" (onClick)="retryRun()"></p-button>
        }
        @if (!['APPLIED', 'CANCELLED', 'FAILED'].includes(run.status)) {
          <p-button label="Cancel Run" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="cancelRun()"></p-button>
        }
        <p-button
          label="Approve & Create Shipments"
          icon="pi pi-check"
          [disabled]="!canApprove || isSaving"
          (onClick)="approve()"></p-button>
      } @else {
        <p-button label="Release Pool" icon="pi pi-list" severity="secondary" [outlined]="true" routerLink="/transport-planning/releases"></p-button>
        @if (!isNew) {
          <p-button label="New Bulk Plan" icon="pi pi-plus" routerLink="/transport-planning/bulk-planning/new"></p-button>
        }
      }
    </div>
  </app-page-hero>

  @if (error) {
    <div class="notice notice--error"><i class="pi pi-exclamation-circle"></i><span>{{ error }}</span></div>
  }
  @if (message) {
    <div class="notice notice--success"><i class="pi pi-check-circle"></i><span>{{ message }}</span></div>
  }
  @if (createdShipmentLinks().length > 0) {
    <div class="notice notice--success shipment-links" aria-label="Created shipments">
      <i class="pi pi-truck"></i>
      <span>Created shipments</span>
      @for (link of createdShipmentLinks(); track link.shipmentId) {
        <a [routerLink]="['/shipment/view', link.shipmentId]">
          <span>Open {{ shipmentLinkLabel(link) }}</span><i class="pi pi-arrow-right"></i>
        </a>
      }
    </div>
  }

  @if (isLoading) {
    <div class="loading-state"><i class="pi pi-spin pi-spinner"></i><span>Loading planning workspace</span></div>
  } @else if (isNew) {
    <div class="wizard-grid">
      <app-page-data-panel
        title="Run Definition"
        subtitle="The selected profile and release set are snapshotted when this run starts."
        [resultText]="selectionSummary()">
        <div class="form-grid">
          <div class="field field--wide">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-layer-group"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select
                  id="bulk-planning-profile"
                  [options]="profiles"
                  [(ngModel)]="selectedProfileId"
                  optionLabel="name"
                  optionValue="profileId"
                  placeholder="Select a profile"
                  [filter]="true"
                  filterBy="name,code"
                  appendTo="body"
                  styleClass="w-full"></p-select>
                <label for="bulk-planning-profile">Planning Profile</label>
              </p-iftaLabel>
              <p-inputGroupAddon>
                <p-button
                  icon="pi pi-pencil"
                  severity="secondary"
                  pTooltip="Edit planning profile"
                  [disabled]="!selectedProfileId"
                  (onClick)="editSelectedProfile()"></p-button>
              </p-inputGroupAddon>
              <p-inputGroupAddon>
                <p-button
                  icon="pi pi-plus"
                  severity="secondary"
                  pTooltip="Create planning profile"
                  (onClick)="profileDraft = newProfile(); profileEditorOpen = true"></p-button>
              </p-inputGroupAddon>
            </p-inputGroup>
            @if (selectedProfile; as profile) {
              <small class="field-help" [class.field-help--warning]="!profile.laneRules.length">
                {{ profileScopeLabel(profile) }}
              </small>
            }
          </div>
          <div class="field field--wide">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-wave-square"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="bulk-run-name" pInputText [(ngModel)]="runName" placeholder="Optional wave or operating shift name">
                <label for="bulk-run-name">Run Name</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
          <div class="field">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-calendar-day"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-datepicker
                  id="bulk-horizon-start"
                  [(ngModel)]="horizonStart"
                  [showTime]="true"
                  [showIcon]="false"
                  hourFormat="24"
                  appendTo="body"
                  styleClass="w-full"></p-datepicker>
                <label for="bulk-horizon-start">Horizon Start</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
          <div class="field">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-calendar-check"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-datepicker
                  id="bulk-horizon-end"
                  [(ngModel)]="horizonEnd"
                  [showTime]="true"
                  [showIcon]="false"
                  hourFormat="24"
                  appendTo="body"
                  styleClass="w-full"></p-datepicker>
                <label for="bulk-horizon-end">Horizon End</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
        </div>
        <div class="run-summary">
          <div>
            <span>Demand selection</span>
            <strong>{{ selectionSummary() }}</strong>
          </div>
          <div>
            <span>Commit model</span>
            <strong>Preview, repair, then approve</strong>
          </div>
          <div>
            <span>Zone policy</span>
            <strong>Explicit active zones only</strong>
          </div>
        </div>
        @if (profileMatchPreview) {
          <section class="match-preview" aria-label="Automatic profile matches">
            <div class="section-heading">
              <div>
                <strong>Automatic Profile Match</strong>
                <span>Directional location-zone lanes determine which profile can consider each release.</span>
              </div>
              <p-button
                label="Refresh Match"
                icon="pi pi-refresh"
                size="small"
                severity="secondary"
                [outlined]="true"
                [loading]="isMatchingProfiles"
                (onClick)="previewProfileMatches()"></p-button>
            </div>
            <div class="match-groups">
              @for (group of profileMatchPreview.groups; track group.profileId) {
                <div class="match-group-wrap">
                  <button type="button" class="match-group" (click)="selectedProfileId = group.profileId">
                    <span><strong>{{ group.profileName }}</strong><small>{{ group.profileCode }}</small></span>
                    <span><strong>{{ group.totalCount }}</strong><small>Matched</small></span>
                    <span class="match-count--eligible"><strong>{{ group.eligibleCount }}</strong><small>Eligible</small></span>
                    <span [class.match-count--blocked]="group.blockedCount"><strong>{{ group.blockedCount }}</strong><small>Blocked</small></span>
                  </button>
                  <details class="match-details">
                    <summary>Review matched releases</summary>
                    @for (release of group.releases; track release.releaseId) {
                      <div class="match-release">
                        <span><strong>{{ release.releaseNumber || ('Release #' + release.releaseId) }}</strong><small>{{ release.sourceNumber || release.laneRuleName }}</small></span>
                        <span>{{ release.originZoneName || 'Zone missing' }} <i class="pi pi-arrow-right"></i> {{ release.destinationZoneName || 'Zone missing' }}</span>
                        <p-tag [value]="release.status" [severity]="statusSeverity(release.status)"></p-tag>
                        @if (release.reasonMessage) { <small>{{ release.reasonMessage }}</small> }
                      </div>
                    }
                  </details>
                </div>
              }
              @if (!profileMatchPreview.groups.length) {
                <div class="match-empty">No automatic profile matched the current release selection.</div>
              }
            </div>
            @if (profileMatchPreview.unmatched.length || profileMatchPreview.ambiguous.length) {
              <div class="match-exceptions">
                @if (profileMatchPreview.unmatched.length) {
                  <span><i class="pi pi-exclamation-circle"></i>{{ profileMatchPreview.unmatched.length }} unmatched</span>
                }
                @if (profileMatchPreview.ambiguous.length) {
                  <span><i class="pi pi-arrows-h"></i>{{ profileMatchPreview.ambiguous.length }} ambiguous</span>
                }
              </div>
              <div class="match-exception-list">
                @for (release of profileMatchPreview.unmatched.concat(profileMatchPreview.ambiguous); track release.releaseId) {
                  <div class="match-release">
                    <span><strong>{{ release.releaseNumber || ('Release #' + release.releaseId) }}</strong><small>{{ release.reasonCode }}</small></span>
                    <span>{{ release.originZoneName || 'Zone missing' }} <i class="pi pi-arrow-right"></i> {{ release.destinationZoneName || 'Zone missing' }}</span>
                    <p-tag [value]="release.status" [severity]="statusSeverity(release.status)"></p-tag>
                    <small>{{ release.reasonMessage }}</small>
                  </div>
                }
              </div>
            }
          </section>
        }
        <div class="panel-command">
          @if (profileMatchPreview?.groups?.length) {
            <p-button
              label="Start Matched Plans"
              icon="pi pi-sitemap"
              severity="secondary"
              [outlined]="true"
              [loading]="isSaving"
              [disabled]="!hasEligibleProfileMatch"
              (onClick)="startMatchedPlans()"></p-button>
          }
          <p-button
            label="Start with Selected Profile"
            icon="pi pi-play"
            [loading]="isSaving"
            [disabled]="!selectedProfileId || !hasDemandSelection"
            (onClick)="createRun()"></p-button>
        </div>
      </app-page-data-panel>

      @if (profileEditorOpen) {
        <app-page-data-panel
          [title]="profileDraft.profileId ? 'Edit Planning Profile' : 'New Planning Profile'"
          subtitle="Define directional zone coverage, capacity policy, optional depot fallbacks, and objectives.">
          <div class="form-grid profile-grid">
            <div class="field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-tag"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="profile-name" pInputText [(ngModel)]="profileDraft.name" placeholder="Riyadh daily owned fleet">
                  <label for="profile-name">Profile Name</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-hashtag"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <input id="profile-code" pInputText [(ngModel)]="profileDraft.code" placeholder="RUH-DAILY">
                  <label for="profile-code">Profile Code</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <label class="checkbox-field checkbox-field--half">
              <p-checkbox [(ngModel)]="profileDraft.autoMatchEnabled" [binary]="true"></p-checkbox>
              <span>Use this profile for automatic matching</span>
            </label>
            <div class="field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-ranking-star"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-inputnumber
                    inputId="profile-match-priority"
                    [(ngModel)]="profileDraft.matchPriority"
                    [min]="-1000"
                    [max]="1000"
                    styleClass="w-full"></p-inputnumber>
                  <label for="profile-match-priority">Match Priority</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-truck"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select
                    id="profile-fleet-scope"
                    [options]="fleetScopeOptions"
                    [(ngModel)]="profileDraft.fleetScope"
                    optionLabel="label"
                    optionValue="value"
                    styleClass="w-full"></p-select>
                  <label for="profile-fleet-scope">Fleet Scope</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-bullseye"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select
                    id="profile-objective"
                    [options]="objectiveOptions"
                    [(ngModel)]="profileDraft.objective"
                    optionLabel="label"
                    optionValue="value"
                    styleClass="w-full"></p-select>
                  <label for="profile-objective">Primary Objective</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-truck-ramp-box"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select
                    id="profile-resource-source"
                    [options]="resourceSourceOptions"
                    [(ngModel)]="profileDraft.resourceSourcePolicy"
                    optionLabel="label"
                    optionValue="value"
                    styleClass="w-full"></p-select>
                  <label for="profile-resource-source">Resource Source</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-arrow-down-1-9"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select
                    id="profile-ranking-strictness"
                    [options]="rankingStrictnessOptions"
                    [(ngModel)]="profileDraft.rankingStrictness"
                    optionLabel="label"
                    optionValue="value"
                    styleClass="w-full"></p-select>
                  <label for="profile-ranking-strictness">Driver & Truck FIFO</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-warehouse"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select
                    id="profile-start-depot"
                    [options]="zonedLocationOptions"
                    [(ngModel)]="profileDraft.defaultStartLocationId"
                    optionLabel="label"
                    optionValue="value"
                    [filter]="true"
                    filterBy="label"
                    [showClear]="true"
                    appendTo="body"
                    styleClass="w-full"></p-select>
                  <label for="profile-start-depot">Default Vehicle Start Depot (Optional)</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-flag-checkered"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select
                    id="profile-end-depot"
                    [options]="zonedLocationOptions"
                    [(ngModel)]="profileDraft.defaultEndLocationId"
                    optionLabel="label"
                    optionValue="value"
                    [filter]="true"
                    filterBy="label"
                    [showClear]="true"
                    appendTo="body"
                    styleClass="w-full"></p-select>
                  <label for="profile-end-depot">Default Vehicle Return Depot (Optional)</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-clock"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-inputnumber
                    inputId="profile-route-hours"
                    [ngModel]="(profileDraft.maximumRouteDurationSeconds || 0) / 3600"
                    (ngModelChange)="profileDraft.maximumRouteDurationSeconds = $event * 3600"
                    [min]="1"
                    [max]="24"
                    styleClass="w-full"></p-inputnumber>
                  <label for="profile-route-hours">Maximum Route Hours</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-money-bill-wave"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-inputnumber
                    inputId="profile-fixed-cost"
                    [(ngModel)]="profileDraft.fixedVehicleCost"
                    [min]="0"
                    mode="decimal"
                    styleClass="w-full"></p-inputnumber>
                  <label for="profile-fixed-cost">Fixed Vehicle Cost</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-shield-halved"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-inputnumber
                    inputId="profile-capacity-buffer"
                    [(ngModel)]="profileDraft.capacityBufferPercent"
                    [min]="0"
                    [max]="100"
                    suffix="%"
                    styleClass="w-full"></p-inputnumber>
                  <label for="profile-capacity-buffer">Capacity Safety Buffer</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-gauge-high"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-inputnumber
                    inputId="profile-target-utilization"
                    [(ngModel)]="profileDraft.minimumTargetUtilizationPercent"
                    [min]="0"
                    [max]="100"
                    suffix="%"
                    styleClass="w-full"></p-inputnumber>
                  <label for="profile-target-utilization">Target Utilization</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-boxes-stacked"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-inputnumber
                    inputId="profile-max-releases"
                    [(ngModel)]="profileDraft.maximumReleasesPerRoute"
                    [min]="1"
                    [max]="100"
                    styleClass="w-full"></p-inputnumber>
                  <label for="profile-max-releases">Maximum Releases per Route</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-location-dot"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-inputnumber
                    inputId="profile-max-stops"
                    [(ngModel)]="profileDraft.maximumServiceStopsPerRoute"
                    [min]="2"
                    [max]="200"
                    styleClass="w-full"></p-inputnumber>
                  <label for="profile-max-stops">Maximum Service Stops per Route</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-list-ol"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-inputnumber
                    inputId="profile-fifo-candidates"
                    [(ngModel)]="profileDraft.maximumFifoCandidates"
                    [min]="1"
                    [max]="500"
                    styleClass="w-full"></p-inputnumber>
                  <label for="profile-fifo-candidates">Maximum FIFO Candidates</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <div class="field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-chart-pie"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select
                    id="profile-allocation-basis"
                    [options]="allocationBasisOptions"
                    [(ngModel)]="profileDraft.projectAllocationBasis"
                    optionLabel="label"
                    optionValue="value"
                    styleClass="w-full"></p-select>
                  <label for="profile-allocation-basis">Project Allocation Basis</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
            <label class="checkbox-field">
              <p-checkbox [(ngModel)]="profileDraft.includeNeighbourZones" [binary]="true"></p-checkbox>
              <span>Allow one-hop neighbouring zones in candidate pools</span>
            </label>
            <section class="lane-editor field--wide">
              <div class="section-heading">
                <div>
                  <strong>Directional Zone Lanes</strong>
                  <span>Pickup and drop-off zones define profile scope. Neighbour matching is configured per side.</span>
                </div>
                <p-button label="Add Lane" icon="pi pi-plus" size="small" severity="secondary" [outlined]="true" (onClick)="addLaneRule()"></p-button>
              </div>
              @if (!profileDraft.laneRules.length) {
                <div class="match-empty">This profile is unscoped and can only be selected manually.</div>
              }
              <div class="lane-list">
                @for (lane of profileDraft.laneRules; track $index; let index = $index) {
                  <div class="lane-row">
                    <div class="lane-row__heading">
                      <strong>Lane {{ index + 1 }}</strong>
                      <label><p-checkbox [(ngModel)]="lane.active" [binary]="true"></p-checkbox><span>Active</span></label>
                      <p-button icon="pi pi-trash" severity="danger" [text]="true" pTooltip="Remove lane" (onClick)="removeLaneRule(index)"></p-button>
                    </div>
                    <div class="field field--wide">
                      <p-inputGroup>
                        <p-inputGroupAddon><i class="fa-solid fa-route"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                          <input [id]="'profile-lane-name-' + index" pInputText [(ngModel)]="lane.name" [placeholder]="'Lane ' + (index + 1)">
                          <label [for]="'profile-lane-name-' + index">Lane Name</label>
                        </p-iftaLabel>
                      </p-inputGroup>
                    </div>
                    <div class="field">
                      <p-inputGroup>
                        <p-inputGroupAddon><i class="fa-solid fa-arrow-up-right-from-square"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                          <p-multiselect
                            [inputId]="'profile-lane-pickup-' + index"
                            [options]="locationZones"
                            [(ngModel)]="lane.pickupZoneIds"
                            optionLabel="name"
                            optionValue="id"
                            [filter]="true"
                            display="chip"
                            appendTo="body"
                            styleClass="w-full"></p-multiselect>
                          <label [for]="'profile-lane-pickup-' + index">Pickup Zones</label>
                        </p-iftaLabel>
                      </p-inputGroup>
                      <label class="inline-check"><p-checkbox [(ngModel)]="lane.includePickupNeighbours" [binary]="true"></p-checkbox><span>Include one-hop pickup neighbours</span></label>
                    </div>
                    <div class="field">
                      <p-inputGroup>
                        <p-inputGroupAddon><i class="fa-solid fa-arrow-down-left"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                          <p-multiselect
                            [inputId]="'profile-lane-dropoff-' + index"
                            [options]="locationZones"
                            [(ngModel)]="lane.dropoffZoneIds"
                            optionLabel="name"
                            optionValue="id"
                            [filter]="true"
                            display="chip"
                            appendTo="body"
                            styleClass="w-full"></p-multiselect>
                          <label [for]="'profile-lane-dropoff-' + index">Drop-off Zones</label>
                        </p-iftaLabel>
                      </p-inputGroup>
                      <label class="inline-check"><p-checkbox [(ngModel)]="lane.includeDropoffNeighbours" [binary]="true"></p-checkbox><span>Include one-hop drop-off neighbours</span></label>
                    </div>
                  </div>
                }
              </div>
            </section>
          </div>
          <div class="panel-command panel-command--split">
            <p-button label="Close" severity="secondary" [outlined]="true" (onClick)="profileEditorOpen = false"></p-button>
            <p-button label="Save Profile" icon="pi pi-save" [loading]="isSaving" (onClick)="saveProfile()"></p-button>
          </div>
        </app-page-data-panel>
      }
    </div>
  } @else if (run) {
    <div class="run-status-line">
      <div>
        <p-tag [value]="run.status" [severity]="statusSeverity(run.status)"></p-tag>
        <strong>{{ run.runName || run.profileName || 'Bulk planning run' }}</strong>
      </div>
      <span>{{ date(run.horizonStart) }} to {{ date(run.horizonEnd) }}</span>
    </div>

    @if (run.status === 'FAILED' && run.errorMessage) {
      <div class="notice notice--error"><i class="pi pi-exclamation-circle"></i><span>{{ run.errorMessage }}</span></div>
    }

    <section class="metric-strip" aria-label="Bulk planning run metrics">
      <div><span>Selected</span><strong>{{ run.selectedReleaseCount }}</strong></div>
      <div><span>Planned</span><strong>{{ run.plannedReleaseCount }}</strong></div>
      <div><span>Unplanned</span><strong>{{ run.unplannedReleaseCount }}</strong></div>
      <div><span>Blocked</span><strong>{{ run.blockedReleaseCount }}</strong></div>
      <div><span>Routes</span><strong>{{ run.routeCount }}</strong></div>
      <div><span>Trucks used</span><strong>{{ run.usedVehicleCount }}</strong></div>
      <div><span>Distance</span><strong>{{ km(run.totalDistanceMeters) }}</strong></div>
      <div><span>Duration</span><strong>{{ duration(run.totalDurationSeconds) }}</strong></div>
    </section>

    @if (['QUEUED', 'RUNNING'].includes(run.status)) {
      <div class="running-state">
        <i class="pi pi-spin pi-spinner"></i>
        <div><strong>Optimizer is processing this demand snapshot</strong><span>The page refreshes automatically.</span></div>
      </div>
    } @else {
      <div class="review-grid">
        <app-page-data-panel
          title="Scenarios"
          subtitle="Compare the same demand snapshot under different objective priorities."
          [resultText]="(run.scenarios?.length || 0) + ' scenarios'">
          <div actions class="scenario-actions">
            <p-button label="Fewer Trucks" icon="pi pi-truck" size="small" severity="secondary" [outlined]="true" [loading]="isSaving" (onClick)="generateScenario('MINIMIZE_TRUCKS')"></p-button>
            <p-button label="Lower Distance" icon="pi pi-map" size="small" severity="secondary" [outlined]="true" [loading]="isSaving" (onClick)="generateScenario('MINIMIZE_DISTANCE')"></p-button>
          </div>
          <div class="scenario-list">
            @for (scenario of run.scenarios || []; track scenario.scenarioId) {
              <button
                type="button"
                class="scenario-row"
                [class.scenario-row--active]="scenario.scenarioId === selectedScenarioId"
                (click)="selectScenario(scenario)">
                <span class="scenario-main">
                  <strong>{{ scenario.scenarioName }}</strong>
                  <small>{{ scenario.objective }}</small>
                </span>
                <span><small>Planned</small><strong>{{ scenario.plannedReleaseCount }}</strong></span>
                <span><small>Trucks</small><strong>{{ scenario.usedVehicleCount }}</strong></span>
                <span><small>Distance</small><strong>{{ km(scenario.totalDistanceMeters) }}</strong></span>
                <span><small>Cost</small><strong>{{ scenario.totalCost == null ? '-' : (scenario.totalCost | number:'1.0-2') }}</strong></span>
                <p-tag [value]="scenario.status" [severity]="statusSeverity(scenario.status)"></p-tag>
              </button>
            }
          </div>
        </app-page-data-panel>

        <app-page-data-panel
          title="Planning Exceptions"
          subtitle="Repair or explicitly exclude every blocking item before approval."
          [resultText]="unresolvedBlockingCount + ' unresolved'">
          <p-table [value]="blockingResults" responsiveLayout="scroll" styleClass="workspace-table">
            <ng-template pTemplate="header">
              <tr>
                <th>Release</th>
                <th>Route</th>
                <th>Reason</th>
                <th>Exclude</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-result>
              <tr>
                <td>
                  <a [routerLink]="['/transport-planning/releases', result.releaseId]">{{ result.releaseNumber || ('Release #' + result.releaseId) }}</a>
                  <small>{{ result.sourceNumber || result.customerName }}</small>
                </td>
                <td>{{ result.originZoneName || result.originName || '-' }} → {{ result.destinationZoneName || result.destinationName || '-' }}</td>
                <td><p-tag [value]="result.reasonCode || result.status" [severity]="statusSeverity(result.status)"></p-tag><small>{{ result.reasonMessage }}</small></td>
                <td>
                  <p-checkbox
                    [binary]="true"
                    [ngModel]="result.explicitlyExcluded"
                    (ngModelChange)="setExcluded(result, $event)"></p-checkbox>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="4" class="empty-cell">No unresolved planning exceptions.</td></tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>
      </div>

      <app-page-data-panel
        title="Route Review"
        subtitle="Lock accepted routes before producing another repair scenario."
        [resultText]="(selectedPlan?.routes?.length || 0) + ' routes'">
        <div actions>
          @if (activeScenario && !isAdjustedScenario && ['REVIEW_READY', 'PARTIAL'].includes(activeScenario.status)) {
            <p-button label="Adjust Plan" icon="pi pi-pencil" size="small" [loading]="isSaving"
              (onClick)="createPlannerCopy()"></p-button>
          }
          @if (isAdjustedScenario) {
            <p-button label="Validate Adjustments" icon="pi pi-check-circle" size="small"
              [loading]="isSaving" (onClick)="validateAdjustedScenario()"></p-button>
          }
          @if (activeScenario && isAdjustedScenario && !['APPLIED', 'CANCELLED', 'FAILED'].includes(run.status)) {
            <p-button
              label="Reoptimize Unlocked"
              icon="pi pi-refresh"
              severity="secondary"
              [outlined]="true"
              size="small"
              [loading]="isSaving"
              (onClick)="reoptimizeUnlocked()"></p-button>
          }
          @if (activeScenario?.planId) {
            <p-button label="Open Full Plan" icon="pi pi-external-link" severity="secondary" [outlined]="true" size="small" [routerLink]="['/transport-planning/plans', activeScenario?.planId]"></p-button>
          }
        </div>
        @if (activeScenario && !isAdjustedScenario && ['REVIEW_READY', 'PARTIAL'].includes(activeScenario.status)) {
          <div class="adjust-plan-bar">
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-comment"></i></p-inputgroup-addon>
              <p-iftalabel>
                <input pInputText id="plannerCopyReason" [(ngModel)]="plannerCopyReason" />
                <label for="plannerCopyReason">Reason for manual adjustment</label>
              </p-iftalabel>
            </p-inputgroup>
            <span>The optimizer scenario remains unchanged; Adjust Plan creates a derived working scenario.</span>
          </div>
        }
        @if (validationMessages.length) {
          <div class="notice notice--error">
            <i class="pi pi-exclamation-circle"></i>
            <span>{{ validationMessages.join(' | ') }}</span>
          </div>
        }
        <div class="route-grid">
          @for (routeItem of selectedPlan?.routes || []; track routeItem.routeId) {
            <article class="route-row">
              <div class="route-sequence">{{ routeItem.routeSequence }}</div>
              <div class="route-resource">
                <div class="route-identity" [title]="routeReference(routeItem)">
                  <small>Route reference</small>
                  <strong><i class="pi pi-directions"></i>{{ routeReference(routeItem) }}</strong>
                </div>
                <div class="route-assignment">
                  <span [title]="routeItem.driverName || ''">
                    <i class="pi pi-user"></i>
                    <small>Driver</small>
                    <strong>{{ routeItem.driverName || ('Driver #' + (routeItem.driverId || '-')) }}</strong>
                  </span>
                  <span [title]="routeItem.vehicleRegistrationNumber || routeItem.vehiclePlateNumber || ''">
                    <i class="pi pi-truck"></i>
                    <small>Vehicle</small>
                    <strong>{{ routeItem.vehiclePlateNumber || routeItem.vehicleRegistrationNumber || ('Vehicle #' + (routeItem.vehicleId || '-')) }}</strong>
                    @if (routeItem.vehicleTypeName) {
                      <em>{{ routeItem.vehicleTypeName }}</em>
                    }
                  </span>
                </div>
                <span>{{ routeReleaseCount(routeItem) }} releases | {{ routeStopCount(routeItem) }} service stops</span>
                <span>Billing: {{ routeItem.billingMode === 'CONSOLIDATED_ZERO_SELL' ? 'Shared · zero sell' : routeItem.billingMode === 'CONSOLIDATED_PER_PARTY' ? 'Shared · per party' : 'Standard' }}</span>
                <span>
                  {{ resourceSourceLabel(routeItem.resourceSource) }}
                  @if (routeItem.fifoRank) { | FIFO #{{ routeItem.fifoRank }} }
                  @if (routeItem.capacityUtilizationPercent != null) {
                    | {{ routeItem.capacityUtilizationPercent | number:'1.0-0' }}% {{ routeItem.bindingCapacityDimension || '' }}
                  }
                </span>
                @if (routeItem.belowTargetUtilization) {
                  <small class="route-warning">Below profile utilization target</small>
                }
              </div>
              <div class="route-stat"><span>Distance</span><strong>{{ km(routeItem.totalDistanceMeters) }}</strong></div>
              <div class="route-stat"><span>Duration</span><strong>{{ duration(routeItem.totalDurationSeconds) }}</strong></div>
              <div class="route-utilization" [class.route-utilization--warning]="routeItem.belowTargetUtilization" aria-label="Capacity utilization">
                <span title="Weight utilization">W {{ utilization(routeItem.usedWeightKg, routeItem.capacityWeightKg) }}</span>
                <span title="Volume utilization">V {{ utilization(routeItem.usedVolumeCbm, routeItem.capacityVolumeCbm) }}</span>
                <span title="Pallet utilization">P {{ utilization(routeItem.usedPallets, routeItem.capacityPallets) }}</span>
                <span title="Unit utilization">U {{ utilization(routeItem.usedUnits, routeItem.capacityUnits) }}</span>
              </div>
              <div class="route-stops">
                @for (stop of routeStopGroups(routeItem); track stop.key) {
                  <span [title]="stop.label">
                    <i [class]="stop.stopType === 'PICKUP' ? 'pi pi-arrow-up-right' : 'pi pi-arrow-down-right'"></i>
                    <strong>{{ stopTypeLabel(stop.stopType) }}</strong>
                    {{ stop.label }}
                    @if (stop.releaseCount > 1) {
                      <small>{{ stop.releaseCount }} releases</small>
                    }
                  </span>
                }
              </div>
              <div class="route-actions">
                <p-button icon="pi pi-sliders-h" label="Review" severity="secondary" [outlined]="true"
                  size="small" (onClick)="toggleRouteDetails(routeItem)"></p-button>
                <p-button
                  [icon]="routeItem.locked ? 'pi pi-lock' : 'pi pi-lock-open'"
                  [label]="routeItem.locked ? 'Locked' : 'Lock'"
                  severity="secondary"
                  [outlined]="!routeItem.locked"
                  size="small"
                  [disabled]="!isAdjustedScenario"
                  (onClick)="toggleRouteLock(routeItem)"></p-button>
              </div>
            </article>
            @if (expandedRouteId === routeItem.routeId) {
              <section class="route-adjustment">
                @if (!isAdjustedScenario) {
                  <div class="route-adjustment__readonly">
                    <i class="pi pi-lock"></i>
                    <span>Create an Adjust Plan scenario to change resources, loads, stops, pricing, or allocations.</span>
                  </div>
                }
                <p-tabs value="loads" styleClass="route-review-tabs">
                  <p-tablist>
                    <p-tab value="loads"><i class="pi pi-list"></i> Loads & Stops</p-tab>
                    <p-tab value="resources"><i class="pi pi-truck"></i> Driver & Vehicle</p-tab>
                    <p-tab value="financials"><i class="pi pi-wallet"></i> Cost & Allocation</p-tab>
                    <p-tab value="validation"><i class="pi pi-shield"></i> Validation & Audit</p-tab>
                  </p-tablist>
                  <p-tabpanels>
                    <p-tabpanel value="loads">
                      <div class="route-review-grid">
                        <div class="review-section">
                          <header><strong>Route demand</strong><span>Move whole releases only.</span></header>
                          @for (release of routeReleases(routeItem); track release.releaseId) {
                            <div class="release-move-row">
                              <div class="route-release-summary">
                                <a [routerLink]="['/transport-planning/releases', release.releaseId]">{{ releaseLabel(release) }}</a>
                                <span><i class="pi pi-briefcase"></i>{{ releaseReferenceContext(release) }}</span>
                                <span><i class="pi pi-building"></i>{{ releaseCustomerContext(release) }}</span>
                                <span><i class="pi pi-box"></i>{{ releaseLoadContext(release) }}</span>
                              </div>
                              <p-inputgroup>
                                <p-inputgroup-addon><i class="pi pi-directions"></i></p-inputgroup-addon>
                                <p-iftalabel>
                                  <p-select [inputId]="'targetRoute-' + routeItem.routeId + '-' + release.releaseId"
                                    [options]="routeOptions(routeItem.routeId)"
                                    [ngModel]="targetRoutes.get(routeItem.routeId + ':' + release.releaseId)"
                                    (ngModelChange)="targetRoutes.set(routeItem.routeId + ':' + release.releaseId, $event)"
                                    optionLabel="label" optionValue="value" appendTo="body"></p-select>
                                  <label [for]="'targetRoute-' + routeItem.routeId + '-' + release.releaseId">Target route</label>
                                </p-iftalabel>
                              </p-inputgroup>
                              <p-button icon="pi pi-arrow-right" label="Move" size="small" [disabled]="!isAdjustedScenario"
                                (onClick)="moveRelease(routeItem, release.releaseId)"></p-button>
                              <a pButton icon="pi pi-users" label="Field resources" size="small" severity="secondary"
                                [outlined]="true" [routerLink]="['/transport-planning/releases', release.releaseId]"></a>
                            </div>
                          }
                        </div>
                        <div class="review-section">
                          <header><strong>Physical sequence</strong><span>Release activities at the same location are consolidated into one service visit.</span></header>
                          @for (stop of physicalStopDrafts(routeItem.routeId!); track stop.key; let index = $index) {
                            <div class="stop-sequence-row">
                              <span>{{ index + 1 }}</span>
                              <i [class]="stop.stopType === 'PICKUP' ? 'pi pi-arrow-up-right' : 'pi pi-arrow-down-right'"></i>
                              <strong>{{ stopTypeLabel(stop.stopType || 'STOP') }}</strong>
                              <div class="physical-stop-detail">
                                <em>{{ stop.label }}</em>
                                <small>{{ stop.releaseCount }} release{{ stop.releaseCount === 1 ? '' : 's' }}: {{ physicalStopReleaseLabels(stop) }}</small>
                              </div>
                              <p-button icon="pi pi-arrow-up" [text]="true" severity="secondary"
                                [disabled]="!isAdjustedScenario || !canMovePhysicalStop(routeItem.routeId!, index, -1)"
                                (onClick)="moveStop(routeItem.routeId!, index, -1)"></p-button>
                              <p-button icon="pi pi-arrow-down" [text]="true" severity="secondary"
                                [disabled]="!isAdjustedScenario || !canMovePhysicalStop(routeItem.routeId!, index, 1)"
                                (onClick)="moveStop(routeItem.routeId!, index, 1)"></p-button>
                            </div>
                          }
                          <p-button label="Save Sequence" icon="pi pi-save" size="small" [disabled]="!isAdjustedScenario"
                            (onClick)="saveStopSequence(routeItem)"></p-button>
                        </div>
                      </div>
                    </p-tabpanel>
                    <p-tabpanel value="resources">
                      <div class="form-grid resource-form">
                        <p-inputgroup>
                          <p-inputgroup-addon><i class="pi pi-user"></i></p-inputgroup-addon>
                          <p-iftalabel>
                            <p-select [inputId]="'driver-' + routeItem.routeId"
                              [options]="resourceOptions.get(routeItem.routeId!)?.drivers || []"
                              [ngModel]="selectedDriverSnapshots.get(routeItem.routeId!)"
                              (ngModelChange)="selectedDriverSnapshots.set(routeItem.routeId!, $event)"
                              optionLabel="label" optionValue="snapshotId" optionDisabled="disabled"
                              [placeholder]="routeItem.driverName || 'No eligible drivers in this plan snapshot'"
                              [disabled]="!isAdjustedScenario" appendTo="body"></p-select>
                            <label [for]="'driver-' + routeItem.routeId">Selected Driver</label>
                          </p-iftalabel>
                        </p-inputgroup>
                        <p-inputgroup>
                          <p-inputgroup-addon><i class="pi pi-truck"></i></p-inputgroup-addon>
                          <p-iftalabel>
                            <p-select [inputId]="'vehicle-' + routeItem.routeId"
                              [options]="resourceOptions.get(routeItem.routeId!)?.vehicles || []"
                              [ngModel]="selectedVehicleSnapshots.get(routeItem.routeId!)"
                              (ngModelChange)="selectedVehicleSnapshots.set(routeItem.routeId!, $event)"
                              optionLabel="label" optionValue="snapshotId" optionDisabled="disabled"
                              [placeholder]="routeItem.vehiclePlateNumber || routeItem.vehicleRegistrationNumber || routeItem.vehicleLabel || 'No eligible vehicles in this plan snapshot'"
                              [disabled]="!isAdjustedScenario" appendTo="body"></p-select>
                            <label [for]="'vehicle-' + routeItem.routeId">Selected Vehicle</label>
                          </p-iftalabel>
                        </p-inputgroup>
                      </div>
                      @if (resourceOptionsLoading.has(routeItem.routeId!)) {
                        <div class="resource-options-state">
                          <i class="pi pi-spin pi-spinner"></i>
                          <span>Checking driver and vehicle eligibility...</span>
                        </div>
                    } @else if (resourceOptionsErrors.has(routeItem.routeId!)) {
                      <div class="resource-options-state resource-options-error">
                        <i class="pi pi-exclamation-circle"></i>
                        <span>{{ resourceOptionsErrors.get(routeItem.routeId!) }}</span>
                          <p-button label="Retry" icon="pi pi-refresh" size="small" severity="secondary"
                            [outlined]="true" (onClick)="loadResourceOptions(routeItem)"></p-button>
                        </div>
                      } @else {
                        <div class="resource-options-state">
                          <i class="pi pi-check-circle"></i>
                          <span><strong>{{ eligibleDriverCount(routeItem.routeId!) }}</strong> eligible drivers</span>
                          <span><strong>{{ eligibleVehicleCount(routeItem.routeId!) }}</strong> eligible vehicles</span>
                          <i class="pi pi-info-circle eligibility-info"
                            [pTooltip]="resourceEligibilityCriteria()" tooltipPosition="top"></i>
                        </div>
                      }
                      <div class="resource-context">
                        <span><strong>Driver:</strong> {{ routeItem.driverName || '-' }}</span>
                        <span><strong>Vehicle:</strong> {{ routeItem.vehiclePlateNumber || routeItem.vehicleRegistrationNumber || '-' }}</span>
                        <span><strong>Source:</strong> {{ resourceSourceLabel(routeItem.resourceSource) }}</span>
                        <span><strong>FIFO:</strong> {{ routeItem.fifoRank || '-' }}</span>
                      </div>
                      @if ((resourceOptions.get(routeItem.routeId!)?.recommendedPairs?.length || 0) > 0) {
                        <div class="recommended-pairs">
                          <strong>Recommended pairs</strong>
                          @for (pair of resourceOptions.get(routeItem.routeId!)?.recommendedPairs || []; track pair.driverSnapshotId + ':' + pair.vehicleSnapshotId) {
                            @if (pair.compatible) {
                              <p-button [label]="pair.driverLabel + ' | ' + pair.vehicleLabel"
                                icon="pi pi-check-circle" severity="secondary" [outlined]="true" size="small"
                                (onClick)="selectRecommendedPair(routeItem.routeId!, pair.driverSnapshotId, pair.vehicleSnapshotId)"></p-button>
                            }
                          }
                        </div>
                      }
                      <p-button label="Apply Resources" icon="pi pi-check" size="small" [disabled]="!isAdjustedScenario"
                        (onClick)="saveRouteResources(routeItem)"></p-button>
                    </p-tabpanel>
                    <p-tabpanel value="financials">
                      <div class="form-grid financial-form">
                        <p-inputgroup>
                          <p-inputgroup-addon><i class="pi pi-money-bill"></i></p-inputgroup-addon>
                          <p-iftalabel>
                            <p-inputnumber [inputId]="'buying-' + routeItem.routeId" mode="currency" currency="SAR"
                              [ngModel]="buyingPrices.get(routeItem.routeId!)"
                              (ngModelChange)="buyingPrices.set(routeItem.routeId!, $event)"></p-inputnumber>
                            <label [for]="'buying-' + routeItem.routeId">Buying / Internal Cost</label>
                          </p-iftalabel>
                        </p-inputgroup>
                        <p-inputgroup>
                          <p-inputgroup-addon><i class="pi pi-chart-line"></i></p-inputgroup-addon>
                          <p-iftalabel>
                            <p-inputnumber [inputId]="'selling-' + routeItem.routeId" mode="currency" currency="SAR"
                              [disabled]="routeItem.billingMode === 'CONSOLIDATED_ZERO_SELL'"
                              [ngModel]="sellingPrices.get(routeItem.routeId!)"
                              (ngModelChange)="sellingPrices.set(routeItem.routeId!, $event)"></p-inputnumber>
                            <label [for]="'selling-' + routeItem.routeId">Selling Price</label>
                          </p-iftalabel>
                        </p-inputgroup>
                        @if (routeItem.billingMode === 'CONSOLIDATED_ZERO_SELL') {
                          <div class="notice span-2">Shared shipment: zero selling and no receivable. Buying and project costs remain available.</div>
                        }
                        <p-inputgroup class="span-2">
                          <p-inputgroup-addon><i class="pi pi-percentage"></i></p-inputgroup-addon>
                          <p-iftalabel>
                            <p-select [inputId]="'allocationBasis-' + routeItem.routeId" [options]="allocationBasisOptions"
                              [ngModel]="allocationBases.get(routeItem.routeId!)"
                              (ngModelChange)="allocationBases.set(routeItem.routeId!, $event)"
                              optionLabel="label" optionValue="value"></p-select>
                            <label [for]="'allocationBasis-' + routeItem.routeId">Project Allocation Basis</label>
                          </p-iftalabel>
                        </p-inputgroup>
                      </div>
                      @if (routeItem.projectAllocationIssue) {
                        <div class="notice notice--warning">
                          <i class="pi pi-exclamation-triangle"></i>
                          <span><strong>Project allocation requires attention.</strong>
                            {{ routeItem.projectAllocationIssue.message }}</span>
                        </div>
                      }
                      <div class="allocation-table">
                        @for (allocation of routeItem.projectAllocations || []; track allocation.logisticsProjectId) {
                          <div>
                            <span><strong>{{ allocation.projectNumber || ('Project #' + allocation.logisticsProjectId) }}</strong>
                              <small>{{ allocation.projectName }}</small></span>
                            <p-inputgroup>
                              <p-inputgroup-addon><i class="pi pi-percentage"></i></p-inputgroup-addon>
                              <p-iftalabel>
                                <p-inputnumber [inputId]="'allocation-' + routeItem.routeId + '-' + allocation.logisticsProjectId"
                                  [(ngModel)]="allocation.allocationPercentage" [min]="0" [max]="100"
                                  [minFractionDigits]="2" [maxFractionDigits]="6"></p-inputnumber>
                                <label [for]="'allocation-' + routeItem.routeId + '-' + allocation.logisticsProjectId">Allocation %</label>
                              </p-iftalabel>
                            </p-inputgroup>
                          </div>
                        } @empty {
                          @if (!routeItem.projectAllocationIssue) {
                            <span>No project allocations are required for this route.</span>
                          }
                        }
                      </div>
                      <div class="financial-summary">
                        <span>Recommended <strong>{{ routeItem.recommendedBuyingPrice == null ? '-' : (routeItem.recommendedBuyingPrice | currency:'SAR') }}</strong></span>
                        <span>Applied <strong>{{ buyingPrices.get(routeItem.routeId!) == null ? '-' : (buyingPrices.get(routeItem.routeId!) | currency:'SAR') }}</strong></span>
                        <span>Source <strong>{{ routeItem.buyingPriceSource || '-' }}</strong></span>
                        @if (routeItem.pricingInternalCostCardId) {
                          <span>Cost card <strong>#{{ routeItem.pricingInternalCostCardId }}</strong></span>
                        }
                        @if (routeItem.pricingContractId) {
                          <span>Contract <strong>#{{ routeItem.pricingContractId }}</strong></span>
                        }
                        @if (routeItem.pricingRateCardId) {
                          <span>Rate card <strong>#{{ routeItem.pricingRateCardId }}</strong></span>
                        }
                        <span>Margin <strong>{{ ((sellingPrices.get(routeItem.routeId!) || 0) - (buyingPrices.get(routeItem.routeId!) || 0)) | currency:'SAR' }}</strong></span>
                        <span>Allocation total <strong [class.invalid-total]="allocationTotal(routeItem) !== 100">{{ allocationTotal(routeItem) | number:'1.2-6' }}%</strong></span>
                      </div>
                      @if (routeItem.pricingWarning) {
                        <div class="notice notice--warning"><i class="pi pi-exclamation-triangle"></i><span>{{ routeItem.pricingWarning }}</span></div>
                      }
                      <p-button label="Save Financials" icon="pi pi-save" size="small" [disabled]="!isAdjustedScenario"
                        (onClick)="saveRouteFinancials(routeItem)"></p-button>
                    </p-tabpanel>
                    <p-tabpanel value="validation">
                      <div class="validation-grid">
                        <div><span>Capacity</span><strong>{{ routeItem.capacityUtilizationPercent | number:'1.0-1' }}% {{ routeItem.bindingCapacityDimension }}</strong></div>
                        <div><span>Maximum releases</span><strong>{{ routeReleaseCount(routeItem) }} / {{ routeItem.maximumReleasesPerRoute || '-' }}</strong></div>
                        <div><span>Maximum stops</span><strong>{{ routeStopCount(routeItem) }} / {{ routeItem.maximumServiceStopsPerRoute || '-' }}</strong></div>
                        <div><span>Route version</span><strong>{{ routeItem.version }}</strong></div>
                      </div>
                      <div class="audit-list">
                        @for (entry of adjustmentAudit; track entry.adjustmentId) {
                          <div><i class="pi pi-history"></i><span><strong>{{ entry.operationType }}</strong>
                            {{ entry.reason }}<small>{{ date(entry.createdAt) }} | {{ entry.actorName || ('Customer #' + (entry.actorCustomerId || '-')) }}</small></span></div>
                        } @empty {
                          <span>No planner adjustments have been recorded for this scenario.</span>
                        }
                      </div>
                    </p-tabpanel>
                  </p-tabpanels>
                </p-tabs>
                @if (isAdjustedScenario) {
                  <p-inputgroup class="adjustment-reason">
                    <p-inputgroup-addon><i class="pi pi-comment"></i></p-inputgroup-addon>
                    <p-iftalabel>
                      <input pInputText [id]="'reason-' + routeItem.routeId"
                        [ngModel]="adjustmentReasons.get(routeItem.routeId!)"
                        (ngModelChange)="adjustmentReasons.set(routeItem.routeId!, $event)" />
                      <label [for]="'reason-' + routeItem.routeId">Required Adjustment Reason</label>
                    </p-iftalabel>
                  </p-inputgroup>
                }
              </section>
            }
          } @empty {
            <div class="empty-state">Select a solved scenario to review its routes.</div>
          }
        </div>
      </app-page-data-panel>
    }
  } @else {
    <app-page-data-panel
      title="Recent Runs"
      subtitle="Open a run to inspect demand outcomes, scenarios, route locks, and execution links."
      [resultText]="runs.length + ' runs'">
      <p-table [value]="runs" responsiveLayout="scroll" styleClass="workspace-table">
        <ng-template pTemplate="header">
          <tr>
            <th>Run</th>
            <th>Horizon</th>
            <th>Demand</th>
            <th>Routes</th>
            <th>Status</th>
            <th></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td><strong>{{ row.runNumber }}</strong><small>{{ row.runName || row.profileName }}</small></td>
            <td>{{ date(row.horizonStart) }}<small>to {{ date(row.horizonEnd) }}</small></td>
            <td>{{ row.plannedReleaseCount }} planned<small>{{ row.unplannedReleaseCount + row.blockedReleaseCount }} exceptions</small></td>
            <td>{{ row.routeCount }} routes<small>{{ row.usedVehicleCount }} trucks</small></td>
            <td><p-tag [value]="row.status" [severity]="statusSeverity(row.status)"></p-tag></td>
            <td class="right"><p-button label="Open" icon="pi pi-arrow-right" [outlined]="true" (onClick)="openRun(row)"></p-button></td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr><td colspan="6" class="empty-cell">No bulk planning runs have been created.</td></tr>
        </ng-template>
      </p-table>
    </app-page-data-panel>
  }
</app-page-shell>
